import { useShallowSelector } from 'components/WorkspaceEditor/Workspace2Store'
import { PROPERTY_EDITOR_SHORTCUT_KEY_MAPPING } from 'pages/ProjectsPage/WorkspacePage/components/constants'
import { useCallback, useState } from 'react'
import { batchUpdate } from 'shared/utils/workspaceUtils'
import { detectContentChanges } from '../../../components/NodeContents/PlainText/plainTextChangeDetection'
import { useNodeMapActions } from '../store/NodeMapStore'
import { UseKeyOperationParams, UseKeyOperationReturn } from '../types'
import {
    focusNodeTarget,
    getEditorReference,
    isArrowDownShortcut,
    isArrowUpShortcut,
    isEnterShortcut,
    isEscapeKeyShortcut,
    isSaveKeyShortcut,
} from '../utils'
import { useAddContentAfterAction } from './useAddContentAfterAction'
import { useExecuteSaveOperation } from './useExecuteSaveOperation'
import { useHandleDeactivateAndAddContent } from './useHandleDeactivateAndAddContent'
import { useNodeNavigation } from './useNodeNavigation'

export const EDITOR_OPERATION_DELAY_MSEC = 100
const { Enter, Save } = PROPERTY_EDITOR_SHORTCUT_KEY_MAPPING

/**
 * Custom hook for handling key operations in content editors
 *
 * This hook manages the complex flow when user presses Enter, Control + S:
 * 1. Save current content via blur handler
 * 2. Add new content after save completes when enter key is pressed
 * 3. Handle different scenarios like deactivation, and adding new content
 * @param params Configuration for key operations
 * @returns key operation controls and handlers
 */
export const useKeyOperation = ({
    editorRef,
    contentItemId,
    handleOnBlur,
    shouldTriggerNewContent,
    setShouldTriggerNewContent,
    unFocusEditor,
    setUnsavedData,
}: UseKeyOperationParams): UseKeyOperationReturn => {
    // Internal state for tracking keydown trigger
    const [fromHandleKeyDown, setFromHandleKeyDown] = useState(false)
    const {
        contentItemViewNodes,
        previousContentItemViewNodes,
        setContentItemViewNodes,
        setTextContentElementToAdd,
        setIsEditingPlainText,
    } = useShallowSelector((s) => ({
        contentItemViewNodes: s.contentItemViewNodes,
        previousContentItemViewNodes: s.previousContentItemViewNodes,
        setContentItemViewNodes: s.actions.setContentItemViewNodes,
        setTextContentElementToAdd: s.actions.setTextContentElementToAdd,
        setIsEditingPlainText: s.actions.setIsEditingPlainText,
    }))

    const { getFocusableContentItem } = useNodeNavigation(contentItemId)
    const { setIsArrowNavigationActive } = useNodeMapActions()

    const {
        handleDeactivateAndAddContentPlainText,
        handleDeactivateAndAddContentReplacementOption,
    } = useHandleDeactivateAndAddContent(
        contentItemId,
        unFocusEditor,
        setShouldTriggerNewContent
    )

    useAddContentAfterAction({
        shouldTriggerNewContent,
        fromHandleKeyDown,
        contentItemId,
        setShouldTriggerNewContent,
        setFromHandleKeyDown,
    })

    const executeSaveOperation = useExecuteSaveOperation({
        editorRef,
        setFromHandleKeyDown,
        handleOnBlur,
    })

    /**
     * Executes the save-then-add-content operation on Enter key press
     * @param event - The keyboard event triggering the operation
     * @returns true if operation was triggered, false otherwise
     */
    const executeSaveAndAddContent = useCallback(
        (
            event: React.KeyboardEvent<HTMLElement>,
            changeDetection?: ReturnType<typeof detectContentChanges>
        ): boolean =>
            executeSaveOperation(
                event,
                isEnterShortcut,
                Enter,
                changeDetection
            ),
        [executeSaveOperation]
    )

    const executeSaveOnly = useCallback(
        (
            event: React.KeyboardEvent<HTMLElement>,
            changeDetection?: ReturnType<typeof detectContentChanges>
        ): boolean =>
            executeSaveOperation(
                event,
                isSaveKeyShortcut,
                Save,
                changeDetection
            ),
        [executeSaveOperation]
    )

    /**
     * Executes the revert-to-previous content operation on Escape key press
     * @param event - The keyboard event triggering the operation
     * @param textElementText - The current text content of the element
     * @param changeDetection - The change detection result for the current content
     * @returns true if operation was triggered, false otherwise
     */
    const executeEscapeKey = useCallback(
        (
            event: React.KeyboardEvent<HTMLElement>,
            textElementText: string,
            changeDetection: ReturnType<typeof detectContentChanges>
        ): boolean => {
            if (!isEscapeKeyShortcut(event)) return false

            const editor = getEditorReference(editorRef, 'Escape key')
            if (!editor) return false

            const {
                shouldRevertToPrevious,
                shouldAddNewContent,
                shouldExecuteOptimistic,
            } = changeDetection

            const backupContentItemViewNodes = [
                ...(shouldExecuteOptimistic
                    ? contentItemViewNodes
                    : previousContentItemViewNodes || []),
            ]

            if (!changeDetection.shouldDeactivate) {
                // Perform state updates in a single batch for better performance
                batchUpdate(() => {
                    // Reset to original state
                    setUnsavedData?.(textElementText)
                    setTextContentElementToAdd(null)
                    setIsEditingPlainText(false)
                    setContentItemViewNodes(backupContentItemViewNodes || [])
                })
            }

            // Handle focus operations outside of batchUpdate to avoid timing issues
            if (shouldRevertToPrevious || shouldAddNewContent) {
                setTimeout(() => {
                    unFocusEditor()
                }, EDITOR_OPERATION_DELAY_MSEC)
            }

            return true
        },
        [
            editorRef,
            previousContentItemViewNodes,
            contentItemViewNodes,
            setUnsavedData,
            setTextContentElementToAdd,
            setIsEditingPlainText,
            setContentItemViewNodes,
            unFocusEditor,
        ]
    )

    /**
     * Executes node navigation on arrow key press
     *
     * "Node navigation" means moving keyboard focus between different content nodes
     * (such as tree nodes) in the workspace editor, not just moving the caret
     * within a multi-line text field. When the user presses the up or down arrow keys,
     * the focus jumps to the previous or next content node, enabling fast keyboard-driven
     * navigation across the editor's structure.
     * @param event - The keyboard event triggering the operation
     * @param treeRef - Optional reference to the virtualized tree for scrolling support
     * @returns true if operation was triggered, false otherwise
     */
    const executeNodeNavigation = useCallback(
        (
            event: React.KeyboardEvent<HTMLElement>,
            treeRef?: {
                current?: {
                    scrollTo?: (options: {
                        key: string | number
                        align?: 'top' | 'bottom' | 'auto'
                        offset?: number
                    }) => void
                }
            }
        ): boolean => {
            const arrowUpOperation = isArrowUpShortcut(event)
            const arrowDownOperation = isArrowDownShortcut(event)
            
            
            if (!arrowUpOperation && !arrowDownOperation) {
                return false
            }

            event.preventDefault() // Prevent default browser behavior

            // Set arrow navigation active flag for CKEditor focus handling
            setIsArrowNavigationActive(true)

            // Get first content item using the hook function
            const {
                contentItem: focusableContentItem,
                nodeKey,
                isArticle,
            } = getFocusableContentItem(event)
            
            
            if (!focusableContentItem) {
                // Reset flag if navigation fails
                setIsArrowNavigationActive(false)
                return false
            }

            // Focus element using the appropriate function based on node type
            setTimeout(async () => {
                await focusNodeTarget(
                    focusableContentItem.id ?? '',
                    nodeKey,
                    isArticle ?? false,
                    true,
                    treeRef,
                    arrowUpOperation ? 'up' : 'down',
                    undefined, // No expansion context available in this hook
                    undefined // No tree data available in this hook
                )
            }, EDITOR_OPERATION_DELAY_MSEC)

            return true
        },
        [contentItemId, getFocusableContentItem, setIsArrowNavigationActive]
    )

    return {
        executeSaveAndAddContent,
        executeSaveOnly,
        executeEscapeKey,
        executeNodeNavigation,
        isEnterKeyOperationInProgress:
            shouldTriggerNewContent && fromHandleKeyDown,
        handleDeactivateAndAddContentPlainText,
        handleDeactivateAndAddContentReplacementOption,
    }
}
